import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import "./index.css";

type Operator = "+" | "-" | "×" | "÷" | null;

export function App() {
  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operator, setOperator] = useState<Operator>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [dark, setDark] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("calculator-dark-mode");
      if (saved !== null) return saved === "true";
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("calculator-dark-mode", String(dark));
  }, [dark]);

  const inputDigit = (digit: string) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === "0" ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay("0.");
      setWaitingForOperand(false);
      return;
    }
    if (!display.includes(".")) {
      setDisplay(display + ".");
    }
  };

  const clear = () => {
    setDisplay("0");
    setPreviousValue(null);
    setOperator(null);
    setWaitingForOperand(false);
  };

  const toggleSign = () => {
    const value = parseFloat(display);
    if (value !== 0) {
      setDisplay(String(-value));
    }
  };

  const inputPercent = () => {
    const value = parseFloat(display);
    setDisplay(String(value / 100));
  };

  const calculate = (left: number, right: number, op: Operator): number => {
    switch (op) {
      case "+": return left + right;
      case "-": return left - right;
      case "×": return left * right;
      case "÷": return right !== 0 ? left / right : 0;
      default: return right;
    }
  };

  const handleOperator = (nextOperator: Operator) => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operator && !waitingForOperand) {
      const result = calculate(previousValue, inputValue, operator);
      const resultStr = parseFloat(result.toFixed(10)).toString();
      setDisplay(resultStr);
      setPreviousValue(result);
    } else {
      setPreviousValue(inputValue);
    }

    setOperator(nextOperator);
    setWaitingForOperand(true);
  };

  const handleEquals = () => {
    if (previousValue === null || operator === null) return;

    const inputValue = parseFloat(display);
    const result = calculate(previousValue, inputValue, operator);
    const resultStr = parseFloat(result.toFixed(10)).toString();
    setDisplay(resultStr);
    setPreviousValue(null);
    setOperator(null);
    setWaitingForOperand(true);
  };

  const formatDisplay = (value: string): string => {
    if (value.includes(".") && !value.endsWith(".")) {
      const [intPart, decPart] = value.split(".");
      const num = parseInt(intPart, 10);
      return num.toLocaleString("en-US") + "." + decPart;
    }
    if (value.endsWith(".")) {
      const num = parseInt(value, 10);
      return num.toLocaleString("en-US") + ".";
    }
    const num = parseFloat(value);
    if (Number.isInteger(num) && !value.includes(".")) {
      return num.toLocaleString("en-US");
    }
    return value;
  };

  const displayFontSize = display.length > 12 ? "text-2xl" : display.length > 8 ? "text-3xl" : "text-5xl";

  const operatorButtons: { label: string; op: Operator }[] = [
    { label: "÷", op: "÷" },
    { label: "×", op: "×" },
    { label: "−", op: "-" },
    { label: "+", op: "+" },
  ];

  const accentBg = dark ? "bg-amber-500" : "bg-red-600";
  const accentHover = dark ? "hover:bg-amber-400" : "hover:bg-red-500";
  const accentText = dark ? "text-zinc-900" : "text-white";
  const activeAccentText = dark ? "text-amber-500" : "text-red-600";
  const activeBg = dark ? "bg-white hover:bg-zinc-100" : "bg-white hover:bg-red-50";

  const numBg = dark ? "bg-zinc-800" : "bg-red-900";
  const numHover = dark ? "hover:bg-zinc-700" : "hover:bg-red-800";
  const fnBg = dark ? "bg-zinc-700" : "bg-red-800";
  const fnHover = dark ? "hover:bg-zinc-600" : "hover:bg-red-700";

  const operatorBtnClass = (op: Operator) =>
    `h-16 rounded-none text-xl font-medium border-0 ${
      operator === op && waitingForOperand
        ? `${activeBg} ${activeAccentText}`
        : `${accentBg} ${accentHover} ${accentText}`
    }`;

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${
      dark
        ? "bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"
        : "bg-gradient-to-br from-red-950 via-red-900 to-red-950"
    }`}>
      <div className="w-full max-w-xs">
        {/* Dark mode toggle */}
        <div className="flex justify-end mb-3">
          <button
            onClick={() => setDark(!dark)}
            className={`p-2 rounded-full transition-colors duration-200 ${
              dark
                ? "bg-zinc-800 text-amber-400 hover:bg-zinc-700"
                : "bg-red-800/60 text-red-200 hover:bg-red-700/60"
            }`}
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {dark ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="4"/>
                <path d="M12 2v2"/>
                <path d="M12 20v2"/>
                <path d="m4.93 4.93 1.41 1.41"/>
                <path d="m17.66 17.66 1.41 1.41"/>
                <path d="M2 12h2"/>
                <path d="M20 12h2"/>
                <path d="m6.34 17.66-1.41 1.41"/>
                <path d="m19.07 4.93-1.41 1.41"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
              </svg>
            )}
          </button>
        </div>

        <Card className={`w-full backdrop-blur-xl shadow-2xl rounded-3xl overflow-hidden transition-colors duration-300 ${
          dark
            ? "bg-zinc-900/80 border-zinc-700"
            : "bg-red-950/80 border-red-800"
        }`}>
          <CardContent className="p-0">
            {/* Display */}
            <div className="px-6 pt-8 pb-4 min-h-[120px] flex flex-col justify-end items-end">
              {operator && previousValue !== null && (
                <div className={`text-sm mb-1 font-mono ${dark ? "text-amber-400/70" : "text-red-400"}`}>
                  {previousValue.toLocaleString("en-US")} {operator}
                </div>
              )}
              <div className={`${displayFontSize} font-light text-white tracking-tight font-mono truncate max-w-full`}>
                {formatDisplay(display)}
              </div>
            </div>

            {/* Buttons */}
            <div className={`grid grid-cols-4 gap-[1px] p-[1px] ${
              dark ? "bg-zinc-700/50" : "bg-red-900/50"
            }`}>
              {/* Row 1: AC, +/-, %, ÷ */}
              <Button
                variant="secondary"
                className={`h-16 rounded-none text-white text-lg font-medium border-0 ${fnBg} ${fnHover}`}
                onClick={clear}
              >
                {display === "0" && previousValue === null ? "AC" : "C"}
              </Button>
              <Button
                variant="secondary"
                className={`h-16 rounded-none text-white text-lg font-medium border-0 ${fnBg} ${fnHover}`}
                onClick={toggleSign}
              >
                +/−
              </Button>
              <Button
                variant="secondary"
                className={`h-16 rounded-none text-white text-lg font-medium border-0 ${fnBg} ${fnHover}`}
                onClick={inputPercent}
              >
                %
              </Button>
              <Button
                className={operatorBtnClass("÷")}
                onClick={() => handleOperator("÷")}
              >
                ÷
              </Button>

              {/* Row 2: 7, 8, 9, × */}
              {["7", "8", "9"].map((d) => (
                <Button
                  key={d}
                  variant="secondary"
                  className={`h-16 rounded-none text-white text-xl font-medium border-0 ${numBg} ${numHover}`}
                  onClick={() => inputDigit(d)}
                >
                  {d}
                </Button>
              ))}
              <Button
                className={operatorBtnClass("×")}
                onClick={() => handleOperator("×")}
              >
                ×
              </Button>

              {/* Row 3: 4, 5, 6, - */}
              {["4", "5", "6"].map((d) => (
                <Button
                  key={d}
                  variant="secondary"
                  className={`h-16 rounded-none text-white text-xl font-medium border-0 ${numBg} ${numHover}`}
                  onClick={() => inputDigit(d)}
                >
                  {d}
                </Button>
              ))}
              <Button
                className={operatorBtnClass("-")}
                onClick={() => handleOperator("-")}
              >
                −
              </Button>

              {/* Row 4: 1, 2, 3, + */}
              {["1", "2", "3"].map((d) => (
                <Button
                  key={d}
                  variant="secondary"
                  className={`h-16 rounded-none text-white text-xl font-medium border-0 ${numBg} ${numHover}`}
                  onClick={() => inputDigit(d)}
                >
                  {d}
                </Button>
              ))}
              <Button
                className={operatorBtnClass("+")}
                onClick={() => handleOperator("+")}
              >
                +
              </Button>

              {/* Row 5: 0 (span 2), ., = */}
              <Button
                variant="secondary"
                className={`h-16 rounded-none text-white text-xl font-medium border-0 col-span-2 ${numBg} ${numHover}`}
                onClick={() => inputDigit("0")}
              >
                0
              </Button>
              <Button
                variant="secondary"
                className={`h-16 rounded-none text-white text-xl font-medium border-0 ${numBg} ${numHover}`}
                onClick={inputDecimal}
              >
                .
              </Button>
              <Button
                className={`h-16 rounded-none text-xl font-medium border-0 ${accentBg} ${accentHover} ${accentText}`}
                onClick={handleEquals}
              >
                =
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default App;
